import axios, { CanceledError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/auth";
import i18n from "@/i18n";

// Default to a relative path so the SPA works on whatever host it is served
// from (mavinty.com today, app.mavinty.com after the DNS/nginx cutover).
// VITE_API_BASE_URL can override this — useful for dev against a remote
// backend, or for embedding the SPA on a different origin.
const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";

export interface SessionBoundRequestConfig extends AxiosRequestConfig {
  expectedSession: string | null;
}

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
});

const requestSessions = new WeakMap<InternalAxiosRequestConfig, string | null>();
const LONG_GENERATION_PATHS = new Set([
  "/plan/macrocycle/rebuild", "/coach/trigger/weekly-review", "/coach/trigger/daily-check",
]);
const PUBLIC_AUTH_PATHS = new Set([
  "/auth/login", "/auth/register", "/auth/password-reset/request", "/auth/password-reset/confirm",
]);
function publicAuth(config?: InternalAxiosRequestConfig): boolean {
  const path = new URL(config?.url ?? "", "https://api.invalid").pathname.replace(/^\/api(?=\/)/, "");
  return config?.method?.toLowerCase() === "post" && PUBLIC_AUTH_PATHS.has(path);
}

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  const bound = config as InternalAxiosRequestConfig & { expectedSession?: string | null };
  if (Object.prototype.hasOwnProperty.call(bound, "expectedSession") && bound.expectedSession !== token) {
    throw new CanceledError("Session changed before dispatch");
  }
  // This internal guard is never sent in headers, query parameters or bodies.
  delete bound.expectedSession;
  const path = new URL(config.url ?? "", "https://api.invalid").pathname.replace(/^\/api(?=\/)/, "");
  if (config.method?.toLowerCase() === "post" && (LONG_GENERATION_PATHS.has(path) || /^\/plan\/weeks\/[1-9][0-9]*\/prepare-details$/.test(path))) {
    config.timeout = 1_260_000;
  }
  requestSessions.set(config, token);
  if (publicAuth(config)) {
    // Signing into another account must not inherit the current demo/expired
    // account's permissions. These endpoints authenticate their own payload.
    config.headers.delete("Authorization");
  } else if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["Accept-Language"] = i18n.language || "en";
  return config;
});

/** Why the paywall turned the athlete away, stashed for /pricing to explain. */
export const PAYWALL_REASON_KEY = "mavinty_paywall_reason";

api.interceptors.response.use(
  (r) => {
    if (requestSessions.get(r.config) !== useAuthStore.getState().token) {
      throw new CanceledError("Session changed");
    }
    return r;
  },
  (err) => {
    if (err.config && requestSessions.has(err.config) &&
        requestSessions.get(err.config) !== useAuthStore.getState().token) {
      return Promise.reject(new CanceledError("Session changed"));
    }
    const status = err.response?.status;

    if (status === 401 && !publicAuth(err.config)) {
      useAuthStore.getState().logout();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }

    // 402 — signed in, but the subscription lapsed or was never started. The
    // backend whitelists /billing, /profile, /auth and /i18n, so /pricing can
    // still load and explain itself; everything else in the app answers 402
    // until there is a live subscription. Without this branch the athlete just
    // sees requests failing with no explanation and no way forward.
    if (status === 402 && typeof window !== "undefined") {
      const reason = err.response?.data?.detail?.reason ?? "no_subscription";
      sessionStorage.setItem(PAYWALL_REASON_KEY, String(reason));
      if (!window.location.pathname.startsWith("/pricing")) {
        window.location.href = "/pricing";
      }
    }

    return Promise.reject(err);
  },
);
